import React from 'react';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';

const AdditionalLocations = ({ locations, onLocationsChange, locationErrors, onLocationErrorsChange }) => {
  // Handle changes for additional locations
  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...locations];
    updatedLocations[index] = { ...updatedLocations[index], [field]: value };
    onLocationsChange(updatedLocations);
    
    // Clear location error when user starts typing
    if (field === 'location' && locationErrors[index]) {
      const newErrors = [...locationErrors];
      newErrors[index] = false;
      onLocationErrorsChange(newErrors);
    }
  };

  // Add a new location
  const addLocation = () => {
    // Check if there are existing locations
    if (locations.length > 0) {
      const lastLocation = locations[locations.length - 1];
      
      // Validate that the last location has a location name
      if (!lastLocation.location.trim()) {
        // Set error for the last location
        const newErrors = [...locationErrors];
        newErrors[locations.length - 1] = true;
        onLocationErrorsChange(newErrors);
        return; // Don't add new location
      }
    }
    
    // Add new location
    onLocationsChange([ ...locations, { location: '', primary_contact: '', primary_contact_number: '', email: '' } ]);
    
    // Add error state for new location (initially no error)
    onLocationErrorsChange([...locationErrors, false]);
  };

  // Remove a location
  const removeLocation = (index) => {
    const updatedLocations = [...locations];
    updatedLocations.splice(index, 1);
    onLocationsChange(updatedLocations);
    
    // Remove error for this location
    const newErrors = [...locationErrors];
    newErrors.splice(index, 1);
    onLocationErrorsChange(newErrors);
  };

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-md font-medium">Additional Locations</h3>
        <button type="button" onClick={addLocation} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center" >
          <span className="mr-1">+</span> Add Location
        </button>
      </div>
      
      {locations.map((location, index) => (
        <div key={index} className="mb-4 p-3 border border-gray-200 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium">Location {index + 1}</h4>
            {/* Always show remove button */}
            <button type="button" onClick={() => removeLocation(index)} className="text-red-600 hover:text-red-800 text-sm" >
              Remove
            </button>
          </div>
          
          {/* Location field - full width textarea */}
          <div className="mb-3">
            <label className="block text-xs font-medium mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <textarea
              value={location.location}
              onChange={(e) => handleLocationChange(index, 'location', e.target.value)}
              className={`w-full p-2 border rounded text-sm ${locationErrors[index] ? 'border-red-500' : ''}`}
              placeholder="Enter location"
              rows={2}
            />
            {locationErrors[index] && (
              <p className="mt-1 text-xs text-red-500">Location is required</p>
            )}
          </div>
          
          {/* Second row with contact fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Primary Contact</label>
              <input
                type="text"
                value={location.primary_contact}
                onChange={(e) => handleLocationChange(index, 'primary_contact', e.target.value)}
                className="w-full p-2 border rounded text-sm"
                placeholder="Contact name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Contact Number</label>
              <input
                type="text"
                value={formatPhoneNumber(location.primary_contact_number)}
                onChange={(e) => handleLocationChange(index, 'primary_contact_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="w-full p-2 border rounded text-sm"
                placeholder="(123) 456-7890"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Email</label>
              <input type="email" value={location.email} onChange={(e) => handleLocationChange(index, 'email', e.target.value)} className="w-full p-2 border rounded text-sm" placeholder="email@example.com" />
            </div>
          </div>
        </div>
      ))}
      
      {locations.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-2">No additional locations added</p>
      )}
    </div>
  );
};

export default AdditionalLocations;
